import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

export interface ComponentItem {
  type: "basic" | "bonus" | "allowance" | "deduction";
  label: string;
  amount: number;
}

interface ContractFormProps {
  onSubmit: (data: {
    startDate: string;
    endDate?: string;
    salary: number;
    components: ComponentItem[];
  }) => void;
  saving: boolean;
}

const ContractForm: React.FC<ContractFormProps> = ({ onSubmit, saving }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [salary, setSalary] = useState<number>(0);
  const [components, setComponents] = useState<ComponentItem[]>([
    { type: "basic", label: "", amount: 0 },
  ]);

  const handleComponentChange = (
    index: number,
    field: keyof ComponentItem,
    value: string
  ) => {
    const updated = [...components];
    updated[index] = {
      ...updated[index],
      [field]: field === "amount" ? parseFloat(value) : (value as any),
    };
    setComponents(updated);
  };

  const addComponent = () => {
    setComponents([
      ...components,
      { type: "allowance", label: "", amount: 0 },
    ]);
  };

  const removeComponent = (index: number) => {
    const updated = [...components];
    updated.splice(index, 1);
    setComponents(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      startDate,
      endDate,
      salary,
      components,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate p={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="End Date (optional)"
            type="date"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Base Salary"
            type="number"
            fullWidth
            value={salary}
            onChange={(e) => setSalary(parseFloat(e.target.value))}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold">
            Components
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        {components.map((component, index) => (
          <Grid container spacing={1} key={index} alignItems="center" mb={1}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Type"
                fullWidth
                value={component.type}
                onChange={(e) =>
                  handleComponentChange(index, "type", e.target.value)
                }
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="bonus">Bonus</MenuItem>
                <MenuItem value="allowance">Allowance</MenuItem>
                <MenuItem value="deduction">Deduction</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Label"
                fullWidth
                value={component.label}
                onChange={(e) =>
                  handleComponentChange(index, "label", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Amount"
                type="number"
                fullWidth
                value={component.amount}
                onChange={(e) =>
                  handleComponentChange(index, "amount", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton
                onClick={() => removeComponent(index)}
                disabled={components.length === 1}
              >
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button
            onClick={addComponent}
            variant="outlined"
            color="primary"
            startIcon={<Add />}
          >
            Add Component
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? "Saving..." : "Submit Contract"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractForm;
